/**
 * Navigation & Background & Mobile Menu - Moon Moon Agency
 * Handles navigation, background filters, and mobile menu interactions
 */

/*********************************************************************************************
 * 🎯 NAVIGATION MODULE
 */
const NavigationManager = {
	// Local configuration
	config: {
		selectors: {
			header: 'header',
			megaMenuLink: '[data-megamenulink]',
			menuLink: '[data-menulink]',
			filter: '[data-filter]',
			cartFilter: '[data-drawer-cart]',
			searchContainer: '[data-search-container]',
			headerBurger: '[data-header-burger]',
			menuMobile: '[data-menu-mobile]',
			menuSublinks: '[data-menu-sublinks]'
		},
		classes: {
			active: 'active',
			open: 'mm-open',
			drawerOpen: 'mm-drawer-open',
			stopScrolling: 'stop-scrolling',
			mobileMenuSlided: 'mm-mobile-menu-elem-slided'
		},
		timing: {
			filterHideDelay: 300
		}
	},

	/**
	 * Initialize navigation systems
	 */
	init() {
		this.initMegaMenus();
		this.initBackgroundFilter();
		this.initAccessibilityHandlers();
		this.initMegaMenuAccessibility();
		this.initMobileSubmenuAccessibility();
		this.initNotifications();
		this.initObfuscatedLinks();
	},

	/**
	 * Initialize SEO-obfuscated menu links.
	 * Rendered by snippets/mm-menu-link.liquid as <span data-mm-obfuscated-link="url">.
	 * Delegated handlers for click + Enter/Space navigate to the stored URL.
	 */
	initObfuscatedLinks() {
		const SELECTOR = '[data-mm-obfuscated-link]';

		document.addEventListener('click', (e) => {
			const el = e.target.closest(SELECTOR);
			if (!el) return;
			const url = el.getAttribute('data-mm-obfuscated-link');
			if (url) window.location.href = url;
		});

		document.addEventListener('keydown', (e) => {
			if (e.key !== 'Enter' && e.key !== ' ') return;
			const el = e.target.closest(SELECTOR);
			if (!el) return;
			e.preventDefault();
			const url = el.getAttribute('data-mm-obfuscated-link');
			if (url) window.location.href = url;
		});

		window.log('info', '🔗 [NAVIGATION]', 'Obfuscated links handler initialized');
	},

	/**
	 * Initialize accessibility keyboard handlers for header buttons
	 */
	initAccessibilityHandlers() {
		// Burger menu button
		const burgerBtn = window.getElement('[data-header-burger]');
		if (burgerBtn) {
			burgerBtn.addEventListener('click', () => {
				window.mmMobileMenu();
				// Update aria-expanded
				const isOpen = burgerBtn.getAttribute('aria-expanded') === 'true';
				burgerBtn.setAttribute('aria-expanded', !isOpen);
			});
		}

		// Search toggle buttons (mobile + desktop)
		window.getElements('[data-search-toggle]').forEach((btn) => {
			btn.addEventListener('click', () => {
				window.mmCloseMobileMenu();
				window.mmShowSearch(btn);
				// Update aria-expanded
				const isOpen = btn.getAttribute('aria-expanded') === 'true';
				btn.setAttribute('aria-expanded', !isOpen);
			});
		});

		// Cart toggle button
		const cartBtn = window.getElement('[data-cart-toggle]');
		if (cartBtn) {
			cartBtn.addEventListener('click', () => {
				window.mmOpenCart(cartBtn);
				window.mmCloseMobileMenu(true);
				window.mmHideSearch(true);
			});

			// Sync aria-expanded with cart open/close state
			document.addEventListener('cart:opened', () => {
				cartBtn.setAttribute('aria-expanded', 'true');
			});
			document.addEventListener('cart:closed', () => {
				cartBtn.setAttribute('aria-expanded', 'false');
			});
		}

		// Global Escape key handler (close all overlays)
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' || e.key === 'Esc') {
				const mobileMenu = window.getElement(this.config.selectors.menuMobile);
				if (mobileMenu?.classList.contains(this.config.classes.open)) {
					window.mmCloseMobileMenu();
					burgerBtn?.focus(); // Return focus to burger button
				}
			}
		});

		window.log('info', 'NavigationManager', 'Accessibility handlers initialized');
	},

	/**
	 * Initialize mega menu and submenu positioning
	 */
	initMegaMenus() {
		const header = window.getElement(this.config.selectors.header);
		if (!header) {
			window.log('warn', 'NavigationManager', 'Header not found');
			return;
		}

		// Defer to next frame to avoid forced reflow during initial paint
		requestAnimationFrame(() => {
			const headerHeight = header.offsetHeight;
			const topValue = `${headerHeight - 1}px`;

			// Position mega menus (data-megamenulink)
			window.getElements(this.config.selectors.megaMenuLink).forEach((menuElem) => {
				const megaMenu = menuElem.nextElementSibling;
				if (megaMenu?.hasAttribute('data-megamenu')) {
					megaMenu.style.top = topValue;
				}
			});

			// Position submenus (data-menulink with sibling data-megamenu)
			window.getElements(this.config.selectors.menuLink).forEach((menuElem) => {
				const submenu = menuElem.nextElementSibling;
				if (submenu && submenu.hasAttribute('data-megamenu')) {
					submenu.style.top = topValue;
				}
			});
		});
	},

	/**
	 * Initialize mega menu keyboard accessibility
	 */
	initMegaMenuAccessibility() {
		const megaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		const regularMenuLinks = window.getElements(this.config.selectors.menuLink);

		// Mega menu links (with dropdown)
		megaMenuLinks.forEach((link) => {
			const megaMenu = link.nextElementSibling;
			if (!megaMenu?.hasAttribute('data-megamenu')) return;

			// Click to toggle mega menu
			link.addEventListener('click', (e) => {
				e.preventDefault();
				this.toggleMegaMenu(link, megaMenu);
			});

			// Keyboard: Enter to toggle, Arrow Down to focus first item
			link.addEventListener('keydown', (e) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					this.toggleMegaMenu(link, megaMenu);
				} else if (e.key === 'ArrowDown') {
					e.preventDefault();
					const isOpen = link.getAttribute('aria-expanded') === 'true';
					if (!isOpen) {
						this.openMegaMenu(link, megaMenu);
					}
					// Focus first link in mega menu
					const firstLink = megaMenu.querySelector('a');
					firstLink?.focus();
				}
			});

			// Hover to open (keep existing behavior)
			link.addEventListener('mouseenter', () => {
				// Close search if open
				window.mmHideSearch?.();
				// Close other mega menus (not this one)
				const allMegaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
				allMegaMenuLinks.forEach((otherLink) => {
					if (otherLink !== link) {
						otherLink.setAttribute('aria-expanded', 'false');
						const otherMegaMenu = otherLink.nextElementSibling;
						if (otherMegaMenu?.hasAttribute('data-megamenu')) {
							otherMegaMenu.style.display = 'none';
						}
					}
				});
				// Open this mega menu
				this.openMegaMenu(link, megaMenu);
			});
		});

		// Regular menu links and submenus
		regularMenuLinks.forEach((link) => {
			// Only add listener if it's NOT a mega menu link
			if (!link.hasAttribute('data-megamenulink')) {
				const sibling = link.nextElementSibling;
				const submenu = sibling && sibling.hasAttribute('data-megamenu') ? sibling : null;

				if (submenu) {
					// This link HAS a submenu - close other mega menus on hover
					link.addEventListener('mouseenter', () => {
						// Close search if open
						window.mmHideSearch?.();
						// Close all mega menus (but CSS will handle submenu display)
						const allMegaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
						allMegaMenuLinks.forEach((megaLink) => {
							megaLink.setAttribute('aria-expanded', 'false');
							const megaMenu = megaLink.nextElementSibling;
							if (megaMenu?.hasAttribute('data-megamenu')) {
								megaMenu.style.display = 'none';
							}
						});
						// Show background filter for submenu too
						this.showBackgroundFilter();
					});

					// Hide background filter when leaving submenu
					link.addEventListener('mouseleave', () => {
						this.hideBackgroundFilter();
					});
				} else {
					// This link has NO dropdown - close all menus on hover
					link.addEventListener('mouseenter', () => {
						window.mmHideSearch?.();
						this.closeAllMegaMenus();
					});
				}
			}
		});

		// Mouse leave header to close all menus
		const header = window.getElement(this.config.selectors.header);
		if (header) {
			header.addEventListener('mouseleave', () => {
				this.closeAllMegaMenus();
			});
		}

		// Escape key closes all mega menus
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' || e.key === 'Esc') {
				const hasOpenMenu = Array.from(megaMenuLinks).some(
					(link) => link.getAttribute('aria-expanded') === 'true'
				);
				if (hasOpenMenu) {
					this.closeAllMegaMenus();
				}
			}
		});

		// Initialize mega menu product links hover/focus
		this.initMegaMenuProductLinks();

		window.log('info', 'NavigationManager', 'Mega menu accessibility initialized');
	},

	/**
	 * Initialize mega menu product links (replace inline onmouseenter)
	 */
	initMegaMenuProductLinks() {
		const productLinks = window.getElements('[data-mega-menu-products-link]');

		productLinks.forEach((link) => {
			const hideSelector = link.dataset.hideSelector;
			const showSelector = link.dataset.showSelector;

			// Hover event
			link.addEventListener('mouseenter', () => {
				this.showMegaMenuProducts(hideSelector, showSelector);
			});

			// Focus event (keyboard navigation)
			link.addEventListener('focus', () => {
				this.showMegaMenuProducts(hideSelector, showSelector);
			});
		});
	},

	/**
	 * Toggle mega menu state
	 */
	toggleMegaMenu(link, megaMenu) {
		const isOpen = link.getAttribute('aria-expanded') === 'true';
		if (isOpen) {
			this.closeMegaMenu(link, megaMenu);
		} else {
			this.closeAllMegaMenus(); // Close others first
			this.openMegaMenu(link, megaMenu);
		}
	},

	/**
	 * Open mega menu
	 */
	openMegaMenu(link, megaMenu) {
		link.setAttribute('aria-expanded', 'true');
		megaMenu.style.display = 'block';
		this.showBackgroundFilter();
	},

	/**
	 * Close mega menu
	 */
	closeMegaMenu(link, megaMenu) {
		link.setAttribute('aria-expanded', 'false');
		megaMenu.style.display = 'none';
	},

	/**
	 * Close all mega menus
	 */
	closeAllMegaMenus() {
		const allMegaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		allMegaMenuLinks.forEach((link) => {
			link.setAttribute('aria-expanded', 'false');
			const megaMenu = link.nextElementSibling;
			if (megaMenu?.hasAttribute('data-megamenu')) {
				megaMenu.style.display = 'none';
			}
		});
		this.hideBackgroundFilter();
	},

	/**
	 * Show specific mega menu products
	 */
	showMegaMenuProducts(classElemsToHide, classElemToShow) {
		try {
			window.getElements(classElemsToHide).forEach((elem) => (elem.style.display = 'none'));
			const elemToShow = window.getElement(classElemToShow, false);
			if (elemToShow) {
				elemToShow.style.display = 'flex';
			}
			window.emit('global:mega-menu-products-switched', { hideSelector: classElemsToHide, showSelector: classElemToShow });
		} catch (error) {
			window.log('error', 'NavigationManager', 'Failed to switch mega menu products', { error: error.message });
		}
	},

	/**
	 * Initialize background filter hover behaviors
	 */
	initBackgroundFilter() {
		const elements = {
			header: window.getElement(this.config.selectors.header),
			searchContainer: window.getElement(this.config.selectors.searchContainer),
			filter: window.getElement(this.config.selectors.filter),
			cart: window.getElement(this.config.selectors.cartFilter)
		};

		if (!elements.header || !elements.filter || !elements.cart) {
			window.log('warn', 'NavigationManager', 'Background filter elements not found');
			return;
		}

		const { header, searchContainer, filter, cart } = elements;
		const megaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		const menuLinks = window.getElements(this.config.selectors.menuLink);

		const toggleFilter = (show) => {
			filter.classList.toggle(this.config.classes.active, show);
			window.emit(`global:background-filter-${show ? 'activated' : 'deactivated'}`, { 
				trigger: show ? 'toggle-on' : 'toggle-off',
				source: 'navigation' 
			});
		};

		// Event listeners
		megaMenuLinks.forEach((link) => 
			link.addEventListener('mouseenter', () => toggleFilter(true))
		);
		
		menuLinks.forEach((link) => 
			link.addEventListener('mouseenter', () => toggleFilter(false))
		);

		header.addEventListener('mouseleave', () => {
			const searchOpen = searchContainer?.classList.contains(this.config.classes.open);
			const cartOpen = cart.classList.contains(this.config.classes.drawerOpen);
			
			if (!searchOpen && !cartOpen) {
				toggleFilter(false);
			}
		});

	},

	/**
	 * Show background filter
	 */
	showBackgroundFilter(zIndex) {
		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) {
			window.log('warn', 'NavigationManager', 'Background filter element not found');
			return;
		}
		
		filter.classList.add(this.config.classes.active);
		if (zIndex) filter.style.zIndex = zIndex;
		window.emit('global:background-filter-activated', { 
			trigger: 'show', 
			zIndex, 
			hasZIndex: !!zIndex 
		});
	},

	/**
	 * Hide background filter
	 */
	hideBackgroundFilter() {
		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) return;
		
		setTimeout(() => filter.style.removeProperty('z-index'), this.config.timing.filterHideDelay);
		filter.classList.remove(this.config.classes.active);
		window.emit('global:background-filter-deactivated', { 
			trigger: 'hide',
			delay: this.config.timing.filterHideDelay 
		});
	},

	/**
	 * Initialize mobile submenu accessibility (replace inline onclick)
	 */
	initMobileSubmenuAccessibility() {
		// Submenu trigger buttons (show next)
		const submenuTriggers = window.getElements('[data-mobile-submenu-trigger]');
		submenuTriggers.forEach((btn) => {
			const targetSelector = btn.dataset.mobileSubmenuTrigger;

			btn.addEventListener('click', () => {
				window.mmMobileMenuShowNext(targetSelector);
				btn.setAttribute('aria-expanded', 'true');
				// Focus sur le bouton retour du sous-menu (les liens de 1er niveau passent en visibility hidden)
				window.getElement(`[data-mobile-submenu-back="${targetSelector}"]`)?.focus();
			});
		});

		// Submenu back buttons (show prev)
		const submenuBackBtns = window.getElements('[data-mobile-submenu-back]');
		submenuBackBtns.forEach((btn) => {
			const targetSelector = btn.dataset.mobileSubmenuBack;

			btn.addEventListener('click', () => {
				window.mmMobileMenuShowPrev(targetSelector);
				// Find corresponding trigger and update aria-expanded
				const trigger = window.getElement(`[data-mobile-submenu-trigger="${targetSelector}"]`);
				if (trigger) {
					trigger.setAttribute('aria-expanded', 'false');
					trigger.focus();
				}
			});
		});

		// Mobile menu close button
		const closeBtn = window.getElement('[data-menu-mobile-close]');
		if (closeBtn) {
			closeBtn.addEventListener('click', () => window.mmCloseMobileMenu());
		}

		window.log('info', 'NavigationManager', 'Mobile submenu accessibility initialized');
	},

	/**
	 * Initialize header notifications toggle
	 */
	initNotifications() {
		const notifBtn = window.getElement('.mm-header-notif-container');
		const notifications = window.getElement('.mm-header-notifications');
		const filter = window.getElement('.mm-header-notification-filter');

		if (!notifBtn || !notifications || !filter) return;

		const toggle = () => {
			const isOpen = notifications.classList.toggle('mm-active');
			filter.classList.toggle('mm-active');
			notifBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
			if (isOpen) window.lockScroll();
			else window.unlockScroll();
		};

		notifBtn.addEventListener('click', toggle);
		filter.addEventListener('click', toggle);

		window.log('info', 'NavigationManager', 'Notifications initialized');
	},

	/**
	 * Handle window resize
	 */
	onResize() {
		this.initMegaMenus();
	}
};

/*********************************************************************************************
 * 📱 MOBILE MENU MODULE
 */
const MobileMenuManager = {
	// Reference to NavigationManager config
	config: NavigationManager.config,

	// Focus trap state
	focusTrap: {
		isActive: false,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Setup focus trap for mobile menu
	 */
	setupFocusTrap(mobileMenu) {
		// Premier et dernier élément recalculés à chaque Tab parmi les éléments atteignables :
		// un sous-menu ouvert masque les liens de 1er niveau (visibility hidden)
		const getFocusable = () => [...mobileMenu.querySelectorAll(
			'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
		)].filter((el) => !el.closest('[inert]') && (!el.checkVisibility || el.checkVisibility({ visibilityProperty: true })));

		// Handle Tab key to trap focus
		this.focusTrap.handleKeyDown = (e) => {
			if (e.key !== 'Tab') return;

			const focusable = getFocusable();
			if (focusable.length === 0) return;
			this.focusTrap.firstFocusable = focusable[0];
			this.focusTrap.lastFocusable = focusable[focusable.length - 1];

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable || !mobileMenu.contains(document.activeElement)) {
					e.preventDefault();
					this.focusTrap.lastFocusable.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable || !mobileMenu.contains(document.activeElement)) {
					e.preventDefault();
					this.focusTrap.firstFocusable.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Focus first element
		getFocusable()[0]?.focus();

		window.log('info', 'MobileMenuManager', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			window.log('info', 'MobileMenuManager', 'Focus trap removed');
		}
	},

	/**
	 * Referme les sous-menus ouverts (le menu se rouvre au 1er niveau)
	 */
	resetSubmenus() {
		window.getElements(`.${this.config.classes.mobileMenuSlided}`).forEach((el) => el.classList.remove(this.config.classes.mobileMenuSlided));
		window.getElements('[data-mobile-submenu-trigger][aria-expanded="true"]').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
	},

	/**
	 * Toggle mobile menu
	 */
	toggle() {
		if (window.innerWidth > window.TABLET_BREAKPOINT && !window.isTouchDevice()) return;

		const header = window.getElement('header');
		const icon = window.getElement(this.config.selectors.headerBurger);
		const mobileMenu = window.getElement(this.config.selectors.menuMobile);
		const headerMenu = window.getElement(this.config.selectors.header);
		const html = document.documentElement;

		if (!header || !icon || !mobileMenu || !headerMenu) {
			window.log('warn', 'MobileMenuManager', 'Required elements not found');
			return;
		}

		const mobileMenuSecondary = window.getElement(this.config.selectors.menuSublinks, false);
		const headerHeight = headerMenu.offsetHeight;
		const topPosition = '0px';
		const heightCalc = 'var(--doc-height)';

		[header, icon, mobileMenu].forEach((el) => el.classList.toggle(this.config.classes.open));

		Object.assign(mobileMenu.style, { top: topPosition, height: heightCalc });
		if (mobileMenuSecondary) mobileMenuSecondary.style.height = heightCalc;

		// Setup focus trap and background filter when menu opens
		if (mobileMenu.classList.contains(this.config.classes.open)) {
			window.lockScroll();
			document.documentElement.classList.add('mm-gorgias-hidden');
			// Use z-index lower than mobile menu (menu is 10, filter should be 5)
			NavigationManager.showBackgroundFilter('var(--index-sm)');
			this.setupFocusTrap(mobileMenu);
		} else {
			window.unlockScroll();
			document.documentElement.classList.remove('mm-gorgias-hidden');
			NavigationManager.hideBackgroundFilter();
			this.removeFocusTrap();
			this.resetSubmenus();
		}

		window.emit('global:mobile-menu-toggled', { headerHeight, topPosition });
	},

	/**
	 * Close mobile menu
	 */
	close(keepFilterActive = false) {
		if (window.innerWidth > window.TABLET_BREAKPOINT && !window.isTouchDevice()) return;

		const elements = {
			header: window.getElement('header'),
			icon: window.getElement(this.config.selectors.headerBurger),
			mobileMenu: window.getElement(this.config.selectors.menuMobile),
			html: document.documentElement
		};

		if (Object.values(elements).some((el) => !el)) {
			window.log('warn', 'MobileMenuManager', 'Required elements not found for closing');
			return;
		}

		const { header, icon, mobileMenu, html } = elements;

		const wasOpen = mobileMenu.classList.contains(this.config.classes.open);
		[header, icon, mobileMenu].forEach((el) => el.classList.remove(this.config.classes.open));
		if (wasOpen) {
			window.unlockScroll();
			html.classList.remove('mm-gorgias-hidden');
		}

		// Remove focus trap when menu closes
		this.removeFocusTrap();
		if (wasOpen) this.resetSubmenus();

		if (!keepFilterActive) NavigationManager.hideBackgroundFilter();
		window.emit('global:mobile-menu-closed', { keepFilterActive });
	},

	/**
	 * Show next sub-menu
	 */
	showNext(elemToShowClass) {
		const element = window.getElement(elemToShowClass, false);
		if (element) {
			element.classList.add(this.config.classes.mobileMenuSlided);
			window.emit('global:mobile-submenu-next', { selector: elemToShowClass });
		}
	},

	/**
	 * Show previous sub-menu
	 */
	showPrev(elemToShowClass) {
		const element = window.getElement(elemToShowClass, false);
		if (element) {
			element.classList.remove(this.config.classes.mobileMenuSlided);
			window.emit('global:mobile-submenu-prev', { selector: elemToShowClass });
		}
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.NavigationManager = NavigationManager;
window.MobileMenuManager = MobileMenuManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
// Navigation functions
window.getMegaMenuPosition = () => NavigationManager.initMegaMenus();
window.mmMegaMenuShowProducts = (hide, show) => NavigationManager.showMegaMenuProducts(hide, show);

// Background filter functions
window.showBgFilter = (zIndex) => NavigationManager.showBackgroundFilter(zIndex);
window.hideBgFilter = () => NavigationManager.hideBackgroundFilter();

// Mobile menu functions
window.mmMobileMenu = () => MobileMenuManager.toggle();
window.mmCloseMobileMenu = (keep) => MobileMenuManager.close(keep);
window.mmMobileMenuShowNext = (elem) => MobileMenuManager.showNext(elem);
window.mmMobileMenuShowPrev = (elem) => MobileMenuManager.showPrev(elem);

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	NavigationManager.init();
});

window.addEventListener('resize', () => {
	NavigationManager.onResize();
});